import apiClient from './apiClient';
import type { ApiResponse } from '../types/api';
import type {
  ClinicSettings,
  PublicClinicSettings,
  UpsertClinicSettingsRequest,
} from '../types/clinic';

export const clinicSettingsService = {
  async getSettings(): Promise<ClinicSettings> {
    const response = await apiClient.get<ApiResponse<ClinicSettings>>('/clinic-settings');
    return response.data.data;
  },

  async getSettingsByDoctor(doctorId: number): Promise<PublicClinicSettings | null> {
    try {
      const response = await apiClient.get<ApiResponse<PublicClinicSettings>>(
        `/clinic-settings/${doctorId}`
      );
      return response.data.data;
    } catch {
      return null;
    }
  },

  async updateSettings(data: Partial<UpsertClinicSettingsRequest>): Promise<ClinicSettings> {
    const response = await apiClient.put<ApiResponse<ClinicSettings>>('/clinic-settings', data);
    return response.data.data;
  },

  async uploadLogo(file: File): Promise<{ logo_path: string; logo_url: string }> {
    const formData = new FormData();
    formData.append('logo', file);
    const response = await apiClient.post<
      ApiResponse<{ logo_path: string; logo_url: string }>
    >('/clinic-settings/logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.data;
  },

  async deleteLogo(): Promise<void> {
    await apiClient.delete('/clinic-settings/logo');
  },

  async uploadCertificate(formData: FormData): Promise<void> {
    await apiClient.post('/clinic-settings/certificate', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  /**
   * Upload letterhead PDF. The PDF becomes the background of the
   * prescription template when the doctor enables the
   * "Imprimir sobre membrete" toggle. Max 5MB, must be PDF.
   */
  async uploadLetterhead(file: File): Promise<{ letterhead_pdf_path: string; letterhead_url: string }> {
    const formData = new FormData();
    formData.append('letterhead', file);
    const response = await apiClient.post<
      ApiResponse<{ letterhead_pdf_path: string; letterhead_url: string }>
    >('/clinic-settings/letterhead', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.data;
  },

  async deleteLetterhead(): Promise<void> {
    await apiClient.delete('/clinic-settings/letterhead');
  },
};

export default clinicSettingsService;
